import React, { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';

/**
 * ChatThinking v2 — premium concierge "thinking" state.
 *
 * Replaces the bouncing-dots trio with a subtle animated gradient shimmer
 * bar (ChatGPT/Airbnb pattern). Two-tier message strategy preserved:
 *   • PRIMARY: instant intent-aware ack ("Crafting your Lisbon getaway…").
 *   • SECONDARY: rotating stage hint ("Scanning 700+ airlines…").
 *
 * The shimmer + typography together read as "thoughtful concierge",
 * not "generic AI spinner". Preserves data-testid for chat-thinking.
 */
export default function ChatThinking({ messages, primary }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!messages?.length) return undefined;
    const id = setInterval(() => setIdx((i) => (i + 1) % messages.length), 1800);
    return () => clearInterval(id);
  }, [messages?.length]);

  if (!messages?.length && !primary) return null;

  return (
    <div className="flex gap-2.5" data-testid="chat-thinking">
      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow-sm flex-shrink-0">
        <Sparkles className="w-3.5 h-3.5 text-white" />
      </div>
      <div className="flex-1 min-w-0 max-w-[85%]">
        {/* Primary intent-aware ack */}
        {primary && (
          <div
            className="text-sm font-semibold text-gray-900 leading-snug"
            data-testid="chat-thinking-primary"
          >
            {primary}
          </div>
        )}
        {/* Shimmer bar — signals real work without gimmicky bounce */}
        <div className="mt-1.5 relative h-1 w-24 rounded-full overflow-hidden bg-gray-100">
          <div
            className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-blue-500 to-transparent"
            style={{ animation: 'shimmer 1.4s ease-in-out infinite' }}
          />
        </div>
        {/* Rotating stage hint */}
        {messages?.length > 0 && (
          <div
            key={idx}
            className="mt-1.5 text-[11px] text-gray-500 font-medium"
            style={{ animation: 'fade-in 240ms ease-out' }}
          >
            {messages[idx]}
          </div>
        )}
      </div>
      <style>{`
        @keyframes shimmer {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(300%); }
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(2px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
